import React, { useEffect, useId, useRef, useState } from 'react';
import socket from '../config/socket';
import { mapImage, mapLabel, mapLogo } from '../mapAssets';

/**
 * Map-Veto als Rad.
 *
 * Erscheint bei allen gleichzeitig, sobald der Admin auf Ready stellt. Klicken
 * darf nur der Kapitaen, der gerade dran ist -- der Server prueft das ohnehin
 * nochmal, hier geht es nur darum, niemanden ins Leere klicken zu lassen.
 *
 * Das Fenster laesst sich schliessen und schrumpft dann auf eine schmale
 * Leiste. Wichtig, weil es sonst dauerhaft ueber der Seite liegt: nach dem
 * Veto kaeme man weder an "Connect to server" noch an die Admin-Knoepfe.
 * Wer als Kapitaen an die Reihe kommt, dem geht es automatisch wieder auf.
 *
 * Die Form kommt aus dem Design-Canvas "Map Veto Wheel v2": ein Kreis aus
 * Kuchenstuecken, der aussen den Countdown als Ring zeigt und sich am Ende so
 * dreht, dass die gespielte Map oben steht. Gebannte Stuecke bekommen einen
 * roten Strich und einen Stempel; beim Bann wackelt das Rad kurz.
 */

/* Geometrie des Rades. Der Innenkreis bleibt frei fuer Countdown und Ergebnis. */
const RI = 98;
const RO = 270;
const RING = 284;
const RING_C = 2 * Math.PI * RING;

const rad = (a) => (a * Math.PI) / 180;
const pt = (r, a) => [r * Math.cos(rad(a)), r * Math.sin(rad(a))];
const f = (n) => n.toFixed(2);

/* Ein Kuchenstueck von Winkel a0 bis a1, innen bei RI ausgeschnitten. */
const wedge = (a0, a1) => {
  const large = a1 - a0 > 180 ? 1 : 0;
  const [x0, y0] = pt(RI, a0);
  const [x1, y1] = pt(RO, a0);
  const [x2, y2] = pt(RO, a1);
  const [x3, y3] = pt(RI, a1);
  return (
    `M${f(x0)} ${f(y0)}L${f(x1)} ${f(y1)}` +
    `A${RO} ${RO} 0 ${large} 1 ${f(x2)} ${f(y2)}` +
    `L${f(x3)} ${f(y3)}A${RI} ${RI} 0 ${large} 0 ${f(x0)} ${f(y0)}Z`
  );
};

/* Jedes Team hat seinen Ton -- dieselben wie in der Kaderliste. */
const TEAM = [
  { line: '#4f9153', ring: 'rgba(79,145,83,.38)', tint: '#f1f7ec', fg: '#33612f', badge: '#dcecd0' },
  { line: '#c08a4e', ring: 'rgba(192,138,78,.38)', tint: '#faf3e9', fg: '#7a5624', badge: '#f3e4ce' },
];

const MUTED = '#6b7a68';
const LINE = '#e4ece0';
const RED = '#c83028';

const closeButtonStyle = {
  font: 'inherit',
  fontSize: 14,
  padding: '7px 14px',
  borderRadius: 6,
  border: '1px solid #d6dfd2',
  background: '#fff',
  color: '#1b1f1c',
  cursor: 'pointer',
};

/* Die Bannliste eines Teams neben dem Rad. */
const TeamPanel = ({ team, name, captain, active, bans }) => (
  <div
    style={{
      order: team === 0 ? 1 : 3,
      minWidth: 0,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      padding: 16,
      borderRadius: 14,
      background: active ? TEAM[team].tint : '#fff',
      border: `1px solid ${active ? TEAM[team].line : LINE}`,
      boxShadow: active ? `0 0 0 3px ${TEAM[team].ring}` : 'none',
      transition: 'background .3s, border-color .3s, box-shadow .3s',
    }}
  >
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, maxWidth: '100%' }}>
        <div style={{ fontSize: 22, fontWeight: 700, overflowWrap: 'anywhere' }}>{name}</div>
        <div style={{ fontSize: 13, color: MUTED }}>Kapitaen: {captain || '-'}</div>
      </div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          padding: '4px 10px',
          borderRadius: 999,
          background: TEAM[team].badge,
          color: TEAM[team].fg,
          opacity: active ? 1 : 0,
          transition: 'opacity .3s',
          whiteSpace: 'nowrap',
        }}
      >
        ist dran
      </div>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {bans.map((b) => (
        <div
          key={b.map}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
            fontSize: 15,
            padding: '8px 0',
            borderTop: '1px solid #eef3ea',
          }}
        >
          <span
            style={{
              color: '#8b978a',
              textDecoration: 'line-through',
              textDecorationColor: RED,
              textDecorationThickness: 2,
            }}
          >
            {mapLabel(b.map)}
          </span>
          <span style={{ fontSize: 12, color: '#8b978a' }}>
            {b.actor === 'auto' ? 'Zeit abgelaufen' : ''}
          </span>
        </div>
      ))}
      {!bans.length && (
        <div style={{ fontSize: 13, color: '#a3ada0', paddingTop: 4 }}>Noch keine Banns</div>
      )}
    </div>
  </div>
);

const MapVeto = ({ veto, identity, teamNames, isAdmin }) => {
  const running = !!(veto && veto.running);
  const winner = veto ? veto.winner : null;
  const turn = veto ? veto.turn : null;
  const captains = veto ? veto.captains : null;
  const bans = (veto && veto.bans) || [];
  const maps = (veto && veto.maps) || [];
  const banCount = bans.length;

  const captainOnTurn = running && captains ? captains[turn] : null;
  const myTurn = !!(
    running &&
    identity &&
    identity.role === 'player' &&
    captainOnTurn &&
    captainOnTurn.playerId === identity.playerId
  );

  const [msLeft, setMsLeft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [minimized, setMinimized] = useState(false);
  const [hover, setHover] = useState(null);
  const [fx, setFx] = useState({ fresh: null, shake: 0, flash: false });
  const [broken, setBroken] = useState({});

  /* clipPath-Ids muessen im Dokument eindeutig sein. */
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');

  /* Countdown lokal herunterzaehlen; massgeblich ist der Zeitpunkt vom Server.
     Alle 100ms, damit der Ring aussen nicht ruckelt. */
  const endsAt = veto ? veto.endsAt : null;
  useEffect(() => {
    if (!running || !endsAt) {
      setMsLeft(null);
      return undefined;
    }

    const tick = () => setMsLeft(Math.max(0, endsAt - Date.now()));

    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [running, endsAt]);

  // Nach jedem Bann die Fehlermeldung wegraeumen.
  useEffect(() => setError(''), [banCount]);

  // Neue Runde oder Ergebnis: wieder aufklappen.
  useEffect(() => setMinimized(false), [running, winner]);

  // Wer dran ist, soll es nicht verpassen.
  useEffect(() => {
    if (myTurn) setMinimized(false);
  }, [myTurn]);

  /* Wackeln und rot aufblitzen, sobald ein Bann dazukommt. Nur wenn die Zahl
     waechst -- beim Neuladen mitten im Veto soll nichts zucken. */
  const seen = useRef(banCount);
  useEffect(() => {
    const grew = banCount > seen.current;
    seen.current = banCount;
    if (!grew) return undefined;

    setHover(null);
    setFx({ fresh: bans[banCount - 1].map, shake: 0, flash: true });

    const timers = [14, -11, 8, -5, 2, 0].map((x, k) =>
      setTimeout(() => setFx((p) => ({ ...p, shake: x })), 230 + k * 45)
    );
    timers.push(setTimeout(() => setFx((p) => ({ ...p, flash: false })), 280));
    timers.push(setTimeout(() => setFx((p) => ({ ...p, fresh: null })), 520));

    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banCount]);

  if (!veto || (!running && !winner)) return null;

  const nameOf = (teamNo) => (teamNo === 0 ? teamNames[0] : teamNames[1]);
  const canClick = running && (myTurn || isAdmin);
  const done = !!winner;

  const ban = (map) => {
    if (!canClick || busy) return;

    setBusy(true);
    setError('');

    socket.emit('veto:ban', map, (result) => {
      setBusy(false);
      if (!result || !result.ok) {
        setError(
          result && result.reason === 'not-your-turn'
            ? 'Du bist gerade nicht dran.'
            : 'Bann nicht moeglich.'
        );
      }
    });
  };

  const bannedBy = {};
  bans.forEach((b) => {
    bannedBy[b.map] = b;
  });

  /* Zusammengeklappt: schmale Leiste unten, der Rest der Seite ist bedienbar. */
  if (minimized) {
    const left = msLeft === null ? null : Math.ceil(msLeft / 1000);
    return (
      <div
        style={{
          position: 'fixed',
          inset: 'auto 0 0 0',
          zIndex: 50,
          display: 'flex',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <button
          type="button"
          onClick={() => setMinimized(false)}
          style={{
            font: 'inherit',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            borderRadius: 999,
            border: 'none',
            background: '#1b1f1c',
            padding: '12px 20px',
            fontSize: 14,
            color: '#fff',
            cursor: 'pointer',
            boxShadow: '0 12px 34px -18px rgba(31,64,32,.8)',
          }}
        >
          {done ? (
            <span>
              Gespielt wird <strong>{mapLabel(winner)}</strong>
            </span>
          ) : (
            <span>
              Map-Veto laeuft
              {captainOnTurn ? ` - ${captainOnTurn.playerName} ist dran` : ''}
              {left !== null ? ` (${left}s)` : ''}
            </span>
          )}
          <span style={{ color: '#a3ada0' }}>oeffnen</span>
        </button>
      </div>
    );
  }

  const N = maps.length;
  const STEP = N ? 360 / N : 360;
  const total = (veto.seconds || 30) * 1000;
  const ms = msLeft === null ? total : msLeft;
  const secs = Math.ceil(ms / 1000);
  const low = secs <= 5 && !done;
  const turnTeam = done ? 0 : turn || 0;

  /* Am Ende dreht sich das Rad so, dass die gespielte Map oben steht. */
  const winnerIdx = done ? maps.indexOf(winner) : -1;
  const wheelRot = winnerIdx >= 0 ? -(winnerIdx * STEP) : 0;

  const segs = maps.map((m, i) => {
    const ac = i * STEP - 90;
    const a0 = ac - STEP / 2 + 0.6;
    const a1 = ac + STEP / 2 - 0.6;

    const b = bannedBy[m];
    const hov = hover === i && !b && !done && canClick;
    const isWin = i === winnerIdx;
    const fresh = !!b && fx.fresh === m;

    /* Frisch gebannt springt heraus, gebannt sinkt ein, Gewinner tritt vor. */
    const out = fresh ? 16 : b ? -6 : hov ? 9 : isWin ? 12 : 0;
    const [ox, oy] = pt(out, ac);
    const [sx1, sy1] = pt(RI + 22, a0 + 6);
    const [sx2, sy2] = pt(RO - 18, a1 - 4);
    const slashLen = Math.hypot(sx2 - sx1, sy2 - sy1);
    const [lx, ly] = pt((RI + RO) / 2 + 4, ac);
    const [cx, cy] = pt((RI + RO) / 2, ac);

    const img = mapImage(m);
    const logo = mapLogo(m);

    return {
      map: m,
      name: mapLabel(m),
      halves: [
        { d: wedge(a0, ac), clipId: `${uid}vc${i}a` },
        { d: wedge(ac, a1), clipId: `${uid}vc${i}b` },
      ],
      img: broken[img] ? null : img,
      ix: f(cx - 150),
      iy: f(cy - 150),
      imgFilter: b ? `url(#${uid}gray)` : 'none',
      logo: broken[logo] ? null : logo,
      logoSize: isWin ? 17 : hov ? 14 : 12.5,
      logoFilter: b ? 'grayscale(1) opacity(0.35)' : 'drop-shadow(0 0.6cqw 1cqw rgba(0,0,0,0.35))',
      tint: fresh
        ? 'rgba(200,48,40,0.6)'
        : b
        ? 'rgba(242,247,239,0.72)'
        : hov
        ? 'rgba(200,48,40,0.14)'
        : 'rgba(255,255,255,0)',
      tintTransition: fresh ? 'fill .12s' : 'fill .6s',
      ox: f(ox),
      oy: f(oy),
      stroke: isWin ? TEAM[0].line : hov ? RED : '#ffffff',
      strokeW: isWin || hov ? 4 : 3,
      cursor: b || done || !canClick ? 'default' : 'pointer',
      sx1: f(sx1),
      sy1: f(sy1),
      sx2: f(sx2),
      sy2: f(sy2),
      slashLen: f(slashLen),
      slashOffset: b ? 0 : f(slashLen),
      slashOpacity: b ? 1 : 0,
      lp: f((lx + ox + 300) / 6),
      tp: f((ly + oy + 300) / 6),
      labelBg: b ? 'rgba(255,255,255,0)' : isWin ? TEAM[0].line : 'rgba(27,31,28,0.86)',
      labelColor: b ? 'rgba(139,151,138,0)' : '#ffffff',
      labelSize: isWin ? 5.6 : 4,
      stampOpacity: b ? 1 : 0,
      stampScale: b ? 0.85 : 2.4,
      byLabel: b ? (b.actor === 'auto' ? 'Zeit abgelaufen' : `${b.actor} · ${nameOf(b.team)}`) : '',
    };
  });

  /* Die Leiste unten: ein Kaestchen pro Bann, zuletzt die gespielte Map. */
  const steps = [];
  for (let k = 0; k < N - 1; k += 1) {
    const b = bans[k];
    const cur = k === banCount && !done;
    /* Wer anfaengt, entscheidet beim Server ein Muenzwurf -- die kommenden
       Schritte zaehlen deshalb von `turn` aus weiter, nicht von Null. */
    const team = b ? b.team : (turn + (k - banCount)) % 2;
    steps.push({
      key: `s${k}`,
      tag: `${k + 1}. Bann · ${nameOf(team)}`,
      map: b ? mapLabel(b.map) : cur ? 'waehlt…' : '–',
      bg: cur ? TEAM[team].tint : '#fff',
      line: cur ? TEAM[team].line : LINE,
      tagColor: TEAM[team].fg,
      mapColor: b ? '#8b978a' : cur ? '#1b1f1c' : '#c3ccc0',
    });
  }
  steps.push({
    key: 'win',
    tag: 'Gespielt wird',
    map: done ? mapLabel(winner) : '–',
    bg: done ? TEAM[0].tint : '#fff',
    line: done ? TEAM[0].line : LINE,
    tagColor: TEAM[0].fg,
    mapColor: done ? '#1b1f1c' : '#c3ccc0',
  });

  const subline = done
    ? 'Alle anderen Maps wurden gebannt.'
    : captainOnTurn
    ? `${captainOnTurn.playerName} bannt fuer ${nameOf(turn)}`
    : `${nameOf(turn)} ist dran -- kein Kapitaen angemeldet, der Admin waehlt.`;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        overflowY: 'auto',
        background: 'rgba(27,31,28,.55)',
        padding: 16,
      }}
    >
      {/* Rot aufblitzen, wenn gebannt wird. */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          background: 'radial-gradient(circle at 50% 45%, rgba(200,48,40,0.28), transparent 65%)',
          opacity: fx.flash ? 1 : 0,
          transition: fx.flash ? 'opacity .05s .2s' : 'opacity .5s',
          zIndex: 3,
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 1320,
          boxSizing: 'border-box',
          background: '#fff',
          border: `1px solid ${LINE}`,
          borderRadius: 18,
          boxShadow: '0 12px 34px -22px rgba(31,64,32,.5)',
          padding: '18px 20px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <button
          type="button"
          onClick={() => setMinimized(true)}
          className="otter-close"
          style={{ ...closeButtonStyle, position: 'absolute', top: 16, right: 16, zIndex: 4 }}
        >
          Schliessen
        </button>

        <header
          className="otter-veto-head"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            marginBottom: 14,
            textAlign: 'center',
            maxWidth: 'calc(100% - 220px)',
          }}
        >
          <h1 style={{ margin: 0, fontSize: 30, fontWeight: 700 }}>
            {done ? 'Gespielt wird' : 'Map-Veto'}
          </h1>
          <p style={{ margin: 0, fontSize: 15, color: MUTED }}>{subline}</p>
          {myTurn && !done && (
            <p
              style={{
                margin: '6px 0 0',
                fontSize: 13,
                fontWeight: 600,
                padding: '4px 12px',
                borderRadius: 999,
                background: TEAM[0].badge,
                color: TEAM[0].fg,
              }}
            >
              Du bist dran -- waehle eine Map zum Bannen.
            </p>
          )}
          {error && (
            <p
              style={{
                margin: '6px 0 0',
                fontSize: 13,
                padding: '6px 12px',
                borderRadius: 8,
                background: '#fdecea',
                color: '#8d241d',
              }}
            >
              {error}
            </p>
          )}
        </header>

        <div
          className="otter-veto-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(150px,220px) minmax(0,1fr) minmax(150px,220px)',
            gap: 20,
            width: '100%',
            alignItems: 'center',
          }}
        >
          <TeamPanel
            team={0}
            name={teamNames[0]}
            captain={captains && captains[0] ? captains[0].playerName : null}
            active={!done && turn === 0}
            bans={bans.filter((b) => b.team === 0)}
          />

          <div style={{ order: 2, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
            <div
              style={{
                width: '100%',
                maxWidth: 'min(760px, max(280px, calc(100vh - 320px)))',
                aspectRatio: '1',
                position: 'relative',
                containerType: 'inline-size',
                transform: `translate(${fx.shake}px, ${-fx.shake * 0.4}px)`,
              }}
            >
              <svg
                viewBox="-300 -300 600 600"
                style={{ width: '100%', height: '100%', overflow: 'visible', display: 'block' }}
              >
                <defs>
                  <pattern
                    id={`${uid}stripes`}
                    width="14"
                    height="14"
                    patternUnits="userSpaceOnUse"
                    patternTransform="rotate(45)"
                  >
                    <rect width="14" height="14" fill="#e5efdd" />
                    <rect width="7" height="14" fill="#eef5e9" />
                  </pattern>
                  <filter id={`${uid}gray`}>
                    <feColorMatrix type="saturate" values="0" />
                  </filter>
                </defs>

                {/* Countdown als Ring aussen. */}
                <circle cx="0" cy="0" r={RING} fill="none" stroke={LINE} strokeWidth="9" />
                <circle
                  cx="0"
                  cy="0"
                  r={RING}
                  fill="none"
                  stroke={done ? TEAM[0].line : low ? RED : TEAM[turnTeam].line}
                  strokeWidth="9"
                  strokeLinecap="round"
                  strokeDasharray={f(RING_C)}
                  strokeDashoffset={done ? 0 : f(RING_C * (1 - ms / total))}
                  transform="rotate(-90)"
                  style={{ transition: 'stroke-dashoffset .1s linear, stroke .3s' }}
                />

                <g
                  style={{
                    transform: `rotate(${wheelRot}deg)`,
                    transition: 'transform 1.4s cubic-bezier(.2,.8,.2,1)',
                  }}
                >
                  {segs.map((s, i) => (
                    <g
                      key={s.map}
                      onClick={() => ban(s.map)}
                      onMouseEnter={() => setHover(i)}
                      onMouseLeave={() => setHover(null)}
                      style={{
                        cursor: s.cursor,
                        transform: `translate(${s.ox}px,${s.oy}px)`,
                        transition: 'transform .45s cubic-bezier(.2,1.4,.4,1)',
                      }}
                    >
                      {s.halves.map((h) => (
                        <g key={h.clipId}>
                          <clipPath id={h.clipId}>
                            <path d={h.d} />
                          </clipPath>
                          <path d={h.d} fill={`url(#${uid}stripes)`} />
                          {s.img && (
                            <image
                              href={s.img}
                              x={s.ix}
                              y={s.iy}
                              width="300"
                              height="300"
                              preserveAspectRatio="xMidYMid slice"
                              clipPath={`url(#${h.clipId})`}
                              filter={s.imgFilter}
                              onError={() => setBroken((p) => ({ ...p, [s.img]: true }))}
                            />
                          )}
                          <path d={h.d} fill={s.tint} style={{ transition: s.tintTransition }} />
                          <path
                            d={h.d}
                            fill="none"
                            stroke={s.stroke}
                            strokeWidth={s.strokeW}
                            style={{ transition: 'stroke .2s' }}
                          />
                        </g>
                      ))}
                      {/* Der rote Strich, der sich beim Bann durchzieht. */}
                      <line
                        x1={s.sx1}
                        y1={s.sy1}
                        x2={s.sx2}
                        y2={s.sy2}
                        stroke={RED}
                        strokeWidth="8"
                        strokeLinecap="round"
                        strokeDasharray={s.slashLen}
                        strokeDashoffset={s.slashOffset}
                        /* Ganz weggeschoben bleibt von der runden Kappe sonst
                           ein roter Punkt am Anfang der Linie stehen. */
                        strokeOpacity={s.slashOpacity}
                        style={{ transition: 'stroke-dashoffset .22s ease-in', pointerEvents: 'none' }}
                      />
                    </g>
                  ))}
                </g>

                <circle cx="0" cy="0" r="94" fill="#ffffff" stroke={LINE} strokeWidth="2" />
              </svg>

              {/* Logos, Namen und Stempel liegen als HTML darueber, damit die
                  Schrift aufrecht bleibt, wenn sich das Rad dreht. */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  transform: `rotate(${wheelRot}deg)`,
                  transition: 'transform 1.4s cubic-bezier(.2,.8,.2,1)',
                }}
              >
                {segs.map((s) => (
                  <div
                    key={s.map}
                    style={{
                      position: 'absolute',
                      left: `${s.lp}%`,
                      top: `${s.tp}%`,
                      width: 0,
                      height: 0,
                      transition:
                        'left .45s cubic-bezier(.2,1.4,.4,1), top .45s cubic-bezier(.2,1.4,.4,1)',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        transform: `translate(-50%,-50%) rotate(${-wheelRot}deg)`,
                        transition: 'transform 1.4s cubic-bezier(.2,.8,.2,1)',
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      {s.logo ? (
                        <img
                          src={s.logo}
                          alt={s.name}
                          onError={() => setBroken((p) => ({ ...p, [s.logo]: true }))}
                          style={{
                            gridArea: '1/1',
                            width: `${s.logoSize}cqw`,
                            height: `${s.logoSize}cqw`,
                            objectFit: 'contain',
                            filter: s.logoFilter,
                            transition:
                              'width .35s cubic-bezier(.2,1.4,.4,1), height .35s cubic-bezier(.2,1.4,.4,1), filter .4s',
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            gridArea: '1/1',
                            fontWeight: 700,
                            fontSize: `${s.labelSize}cqw`,
                            padding: '0.5cqw 1.8cqw',
                            borderRadius: 999,
                            background: s.labelBg,
                            color: s.labelColor,
                            whiteSpace: 'nowrap',
                            transition: 'background .4s, color .4s, font-size .6s',
                          }}
                        >
                          {s.name}
                        </div>
                      )}

                      <div
                        style={{
                          gridArea: '1/1',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '0.8cqw',
                          opacity: s.stampOpacity,
                          transform: `rotate(-14deg) scale(${s.stampScale})`,
                          transition:
                            'opacity .15s .2s, transform .35s .2s cubic-bezier(.3,1.8,.5,1)',
                        }}
                      >
                        <div
                          style={{
                            padding: '0.3cqw 2cqw',
                            border: `0.55cqw solid ${RED}`,
                            borderRadius: '0.8cqw',
                            background: 'rgba(255,255,255,0.92)',
                            color: RED,
                            fontWeight: 800,
                            fontSize: '4.4cqw',
                            letterSpacing: '0.1em',
                            lineHeight: 1.15,
                          }}
                        >
                          GEBANNT
                        </div>
                        <div
                          style={{
                            fontSize: 'max(10px,1.9cqw)',
                            fontWeight: 600,
                            color: '#1b1f1c',
                            background: 'rgba(255,255,255,0.85)',
                            padding: '0.2cqw 1cqw',
                            borderRadius: '0.6cqw',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {s.byLabel}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Countdown bzw. Ergebnis in der Mitte. */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.4cqw',
                }}
              >
                <div
                  style={{
                    fontSize: 'max(10px,2cqw)',
                    fontWeight: 600,
                    color: done ? TEAM[0].fg : TEAM[turnTeam].fg,
                    maxWidth: '28cqw',
                    textAlign: 'center',
                  }}
                >
                  {done ? 'Entschieden' : `${nameOf(turn)} ist dran`}
                </div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: `${done ? 6.4 : 10}cqw`,
                    lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums',
                    color: low ? RED : '#1b1f1c',
                    transition: 'color .3s',
                  }}
                >
                  {done ? mapLabel(winner) : `${secs}s`}
                </div>
                <div style={{ fontSize: 'max(10px,2cqw)', color: '#8b978a' }}>
                  {done ? '' : `${banCount} von ${N - 1} Banns`}
                </div>
              </div>
            </div>
          </div>

          <TeamPanel
            team={1}
            name={teamNames[1]}
            captain={captains && captains[1] ? captains[1].playerName : null}
            active={!done && turn === 1}
            bans={bans.filter((b) => b.team === 1)}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 8,
            marginTop: 14,
            width: '100%',
          }}
        >
          {steps.map((st) => (
            <div
              key={st.key}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                minWidth: 88,
                padding: '6px 10px',
                borderRadius: 10,
                background: st.bg,
                border: `1px solid ${st.line}`,
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 600, color: st.tagColor }}>{st.tag}</span>
              <span style={{ fontSize: 15, fontWeight: 600, color: st.mapColor }}>{st.map}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MapVeto;
